"use client";

import { useCallback, JSX, ReactNode, MouseEvent } from "react";

import Badge from "~/componentsDS/Badge";
import Button from "~/componentsDS/Button";
import Container from "~/componentsDS/Layout/Container";
import Reveal from "~/componentsDS/Reveal";
import Heading from "~/componentsDS/Typography/Heading";
import Carousel from "~/componentsDS/Carousel";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import HowItWorksStepCard from "./components/HowItWorksStepCard";
import { QuizCTA } from "~/components/features/quiz";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { CTA_TITLE, data } from "./data";
import { cn } from "~/lib/utils";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { getTextFromNode } from "~/utils/getTextFromNode";

export type StepItem = {
    imageUrl: string;
    imageAlt?: string;
    icon: JSX.Element;
    title: string;
    description: ReactNode;
}

// TODO: Group related props into objects (e.g. cta.title and cta.link)
export type Props = {
    title?: ReactNode;
    subtitle?: string;
    items?: StepItem[];
    ctaTitle?: ReactNode;
    ctaLink?: string;
    ctaClassName?: string;
    onCTAClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
    event_name?: string;
    widget?: string;
    className?: string;
    showMobileBenefitsText?: boolean;
    showCTA?: boolean;
    resolveCtaQuizLink?: boolean;
    /** The CTA's click is being held (e.g. waiting on a cart write): dim it, mark it aria-disabled and skip its GTM event. */
    isCtaHeld?: boolean;
}

const HowItWorksSection = ({
    title = "How to get started?",
    subtitle = "How it works",
    items = data,
    ctaTitle,
    ctaLink,
    ctaClassName,
    onCTAClick,
    event_name = GTM_EVENT_NAMES.HP_INTERACTION,
    widget = "how_it_works",
    className,
    showMobileBenefitsText = true,
    showCTA = true,
    resolveCtaQuizLink = true,
    isCtaHeld = false,
}: Props) => {
    const sendEvent = useTrackEvent();
    const CtaComponent = resolveCtaQuizLink ? QuizCTA : Button;

    const onSelect = useCallback((emblaApi: CarouselApi) => {
        if (!emblaApi) return;

        const idx = emblaApi.selectedScrollSnap();
        const item = items[idx];

        if (!item) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name,
                widget,
                meta_data: {
                    image_title: item.title,
                    image_count: idx + 1,
                },
            },
        });
    }, [sendEvent, items, event_name, widget]);

    return (
        <section className={cn("py-16 md:py-12", className)}>
            <Container>
                <Reveal>
                    <Badge className="mb-4 font-medium">{subtitle}</Badge>
                    <Heading as="h2" size="sm" sizes={{md: "md"}} className="text-primary500 mb-6 lg:mb-8">
                        {title}
                    </Heading>
                </Reveal>

                <div className="w-[100vw] md:w-full overflow-hidden mb-8 md:mb-12 max-md:-mx-6 max-md:px-6">
                    <Carousel
                        onSelect={onSelect}
                        loop={false}
                        variant="compact"
                        compactPaginationAlign="right"
                        compactPaginationBg="bg-trueBlack/10 backdrop-blur-[25px]"
                        compactPaginationDotColor="bg-gray300"
                        opts={{
                            align: "center",
                            skipSnaps: false,
                            dragFree: false
                        }}
                        className="w-full"
                    >
                        <CarouselContentVisible className="max-md:ml-[-6px] md:ml-[-26px]">
                            {items.map((step, index) => (
                                <CarouselItem
                                    key={index}
                                    className="pl-[6px] md:pl-[26px] flex-shrink-0 basis-full sm:basis-[500px] xl:basis-1/3"
                                >
                                    <HowItWorksStepCard
                                        stepNumber={index + 1}
                                        {...step}
                                    />
                                </CarouselItem>
                            ))}
                        </CarouselContentVisible>
                    </Carousel>
                </div>

                <div className="flex flex-col items-center">
                    {showCTA && (
                        <CtaComponent
                            onClick={onCTAClick}
                            variant="primary"
                            aria-disabled={isCtaHeld}
                            eventData={isCtaHeld ? undefined : {
                                widget,
                                event_name,
                                meta_data: {
                                    click_text: getTextFromNode(ctaTitle ?? CTA_TITLE),
                                },
                            }}
                            href={ctaLink ?? QUIZ_ROUTES.HORMONE_IMBALANCE}
                            size="md"
                            className={cn(ctaClassName, isCtaHeld && "opacity-40")}
                        >
                            {ctaTitle ?? CTA_TITLE}
                        </CtaComponent>
                    )}
                    {showMobileBenefitsText && <Paragraph className="md:hidden text-gray400 mt-2" size="xs">Free shipping. Cancel anytime. HSA/FSA Eligible.</Paragraph>}
                </div>
            </Container>
        </section>
    )
};

export default HowItWorksSection;
